import {
  computed,
  inject,
  Injectable,
  InjectionToken,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDrawerMode } from '@angular/material/sidenav';
import {
  ActivatedRoute,
  IsActiveMatchOptions,
  NavigationEnd,
  NavigationSkipped,
  Router,
  UrlTree,
} from '@angular/router';
import { MAP_ROOT } from '@lassi/config';
import { filter, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';

export const ROUTE_STORAGE = new InjectionToken<Storage>('ROUTE_STORAGE');

@Injectable({
  providedIn: 'root',
})
export class RouteUtilsService {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly routeStorage = inject(ROUTE_STORAGE);

  readonly mapRoot = inject(MAP_ROOT);
  static readonly MAP_MENU_OUTLET_NAME = 'menu';
  static readonly MAP_MENU_ROOT_PATH = 'landing';
  static readonly MAP_MENU_LAST_VISITED_SECTION_KEY = 'menu.last-visited';

  static readonly VICNAMES_ADMIN_PATH = 'vicnames/admin';
  static readonly FORCE_ONBOARDING_QUERY_KEY = 'tour';

  private readonly LAST_URL_KEY = 'lastMapUrl';
  private readonly EXCLUDED_TRACK_LAST_URLS = ['/help'];

  routeEvents$ = this.router.events.pipe(
    filter((e) => e instanceof NavigationEnd || e instanceof NavigationSkipped),
    tap((event) => {
      if (event instanceof NavigationEnd) {
        this.trackPreviousUrl(event.url);
      }
    }),
    shareReplay(1),
  );

  route = toSignal(this.routeEvents$.pipe(map(() => this.router.url)));

  goBackUrl = signal<UrlTree | null>(null);

  leafRoute$ = this.routeEvents$.pipe(
    switchMap(() => {
      let currentRoute = this.activatedRoute;
      while (currentRoute.firstChild) {
        currentRoute = currentRoute.firstChild;
      }
      return of(currentRoute);
    }),
  );

  leafRouteData$ = this.leafRoute$.pipe(
    switchMap(
      (
        route,
      ): Observable<
        Partial<{
          title: string;
          drawerBack: string;
          bottomSheetBack: string;
          drawerSize?: string;
          drawerMode?: MatDrawerMode;
        }>
      > => route.data,
    ),
  );

  isBottomSheetLandingPage = toSignal(
    this.isActive(
      this.router.createUrlTree([
        this.mapRoot,
        {
          outlets: {
            [RouteUtilsService.MAP_MENU_OUTLET_NAME]: [
              RouteUtilsService.MAP_MENU_ROOT_PATH,
            ],
          },
        },
      ]),
      { paths: 'exact' },
    ),
  );

  isMapPage = toSignal(
    this.isActive(this.router.createUrlTree([this.mapRoot]), {
      paths: 'subset',
    }),
  );

  isRegisterPage = toSignal(
    this.isActive(this.router.createUrlTree(['register']), { paths: 'exact' }),
  );

  isLoginPage = toSignal(
    this.isActive(this.router.createUrlTree(['login']), { paths: 'exact' }),
  );

  private readonly isReviewSmesSubmissionList = toSignal(
    this.isActive(
      this.router.createUrlTree([
        this.mapRoot,
        {
          outlets: {
            [RouteUtilsService.MAP_MENU_OUTLET_NAME]: [
              RouteUtilsService.MAP_MENU_ROOT_PATH,
              'smes',
              'admin',
              'submissions',
            ],
          },
        },
      ]),
      { paths: 'exact' },
    ),
  );

  private readonly isReviewSmesSubmissionOrList = toSignal(
    this.isActive(
      this.router.createUrlTree([
        this.mapRoot,
        {
          outlets: {
            [RouteUtilsService.MAP_MENU_OUTLET_NAME]: [
              RouteUtilsService.MAP_MENU_ROOT_PATH,
              'smes',
              'admin',
              'submissions',
            ],
          },
        },
      ]),
      { paths: 'subset' },
    ),
  );

  isReviewSmesSubmission = computed(
    () =>
      this.isReviewSmesSubmissionOrList() && !this.isReviewSmesSubmissionList(),
  );

  private isActive(
    urlTree: UrlTree,
    options: Pick<IsActiveMatchOptions, 'paths'>,
  ) {
    return this.routeEvents$.pipe(
      map(() =>
        this.router.isActive(urlTree, {
          matrixParams: 'ignored',
          queryParams: 'ignored',
          fragment: 'ignored',
          paths: options.paths,
        }),
      ),
    );
  }

  isDrawerPageActive(
    path: string[],
    paths: IsActiveMatchOptions['paths'] = 'subset',
  ) {
    return this.isActive(
      this.router.createUrlTree([
        this.mapRoot,
        {
          outlets: {
            [RouteUtilsService.MAP_MENU_OUTLET_NAME]: [
              RouteUtilsService.MAP_MENU_ROOT_PATH,
              ...path,
            ],
          },
        },
      ]),
      {
        paths,
      },
    );
  }

  isDrawerActive = toSignal(
    this.isActive(
      this.router.createUrlTree([
        this.mapRoot,
        {
          outlets: {
            [RouteUtilsService.MAP_MENU_OUTLET_NAME]:
              RouteUtilsService.MAP_MENU_ROOT_PATH,
          },
        },
      ]),
      {
        paths: 'subset',
      },
    ),
  );

  getEditMarkRoute(mark_id: number) {
    return [
      '/',
      this.mapRoot,
      {
        outlets: {
          [RouteUtilsService.MAP_MENU_OUTLET_NAME]: [
            RouteUtilsService.MAP_MENU_ROOT_PATH,
            'smes',
            'admin',
            'manage',
            mark_id,
          ],
        },
      },
    ];
  }

  goToLayerManagement() {
    return this.router.navigate(
      [
        this.mapRoot,
        {
          outlets: {
            [RouteUtilsService.MAP_MENU_OUTLET_NAME]: [
              RouteUtilsService.MAP_MENU_ROOT_PATH,
              'layers',
            ],
          },
        },
      ],
      {
        queryParamsHandling: 'preserve',
        replaceUrl: true,
      },
    );
  }

  getQueryParamsFromString(href: string): Record<string, string> {
    const urlObj = new URL(href, window.location.origin);
    const queryParams: Record<string, string> = {};
    urlObj.searchParams.forEach((value, key) => {
      queryParams[key] = value;
    });

    return queryParams;
  }

  goToMap() {
    return this.router.navigate([this.mapRoot], {
      queryParamsHandling: 'merge',
      queryParams: {
        q: undefined,
        addrSearchFormData: undefined,
      },
    });
  }

  goToMapWithGoBackButton(goBackUrl: UrlTree) {
    this.goBackUrl.set(goBackUrl);
    return this.goToMap();
  }

  async handleGoBack() {
    const url = this.goBackUrl();

    if (url) {
      if (await this.router.navigateByUrl(url)) {
        this.goBackUrl.set(null);
      }
    }
  }

  goToLogout() {
    return this.router.navigate(['/logout'], {
      queryParams: {
        redirectTo: this.route(),
      },
      replaceUrl: true,
    });
  }

  private trackPreviousUrl(url: string): void {
    if (!this.shouldExcludeFromTracking(url)) {
      const fullUrl = `${window.location.origin}${url}`;
      this.routeStorage.setItem(this.LAST_URL_KEY, fullUrl);
    }
  }

  private shouldExcludeFromTracking(url: string): boolean {
    return this.EXCLUDED_TRACK_LAST_URLS.some((excludedRoute) =>
      url.includes(excludedRoute),
    );
  }

  getLastMapUrl(): string | null {
    return this.routeStorage.getItem(this.LAST_URL_KEY);
  }

  clearLastMapUrl(): void {
    this.routeStorage.removeItem(this.LAST_URL_KEY);
  }
}
